import React, { useState } from 'react';
import { 
  format, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  isSameMonth, 
  isSameDay, 
  addDays, 
  isBefore,
  isAfter,
  parseISO
} from 'date-fns';
import { FiChevronLeft, FiChevronRight, FiInfo } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const AvailabilityCalendar = ({ bookedDates = [], onSelectRange }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedRange, setSelectedRange] = useState({ start: null, end: null });

  const renderHeader = () => {
    return (
      <div className="flex items-center justify-between px-4 py-6 bg-brand-olive text-brand-cream rounded-t-sm">
        <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2 hover:bg-white/10 rounded-full transition-colors">
          <SafeIcon icon={FiChevronLeft} className="text-xl" />
        </button>
        <h2 className="font-serif text-2xl font-bold uppercase tracking-widest">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 hover:bg-white/10 rounded-full transition-colors">
          <SafeIcon icon={FiChevronRight} className="text-xl" />
        </button>
      </div>
    );
  };

  const renderDays = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return (
      <div className="grid grid-cols-7 mb-2 border-b border-brand-light/30">
        {days.map((day, i) => (
          <div key={i} className="py-3 text-center text-[10px] uppercase tracking-[0.2em] font-bold text-brand-sage">
            {day}
          </div>
        ))}
      </div>
    );
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;
    let formattedDate = "";

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, "d");
        const cloneDay = day;
        
        // Check if date is booked
        const isBooked = bookedDates.some(range => 
          (isAfter(cloneDay, parseISO(range.start)) || isSameDay(cloneDay, parseISO(range.start))) && 
          (isBefore(cloneDay, parseISO(range.end)) || isSameDay(cloneDay, parseISO(range.end)))
        );

        const isPast = isBefore(cloneDay, startOfMonth(new Date())) && !isSameDay(cloneDay, new Date());
        const isToday = isSameDay(cloneDay, new Date());

        days.push(
          <div
            key={day.toString()}
            className={`relative h-16 md:h-20 border-r border-b border-brand-light/10 flex flex-col items-center justify-center transition-all cursor-pointer
              ${!isSameMonth(day, monthStart) ? 'bg-brand-cream/30 text-brand-light' : 'text-brand-dark'}
              ${isBooked ? 'bg-red-50/50 cursor-not-allowed' : 'hover:bg-brand-olive/5'}
              ${isToday ? 'bg-brand-cream font-bold' : ''}
            `}
            onClick={() => !isBooked && !isPast && onSelectRange && onSelectRange(cloneDay)}
          >
            <span className={`z-10 text-sm ${isToday ? 'text-brand-olive' : ''}`}>{formattedDate}</span>
            {isBooked && (
              <div className="absolute inset-0 flex items-center justify-center opacity-20">
                <div className="w-full h-[1px] bg-red-800 rotate-45 absolute"></div>
                <div className="w-full h-[1px] bg-red-800 -rotate-45 absolute"></div>
              </div>
            )}
            {isToday && <div className="absolute top-2 right-2 w-1.5 h-1.5 bg-brand-olive rounded-full"></div>}
            {isBooked && <span className="absolute bottom-2 text-[8px] uppercase tracking-tighter text-red-600 font-bold">Booked</span>}
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div className="grid grid-cols-7" key={day.toString()}>
          {days}
        </div>
      );
      days = [];
    }
    return <div className="bg-white">{rows}</div>;
  };

  return (
    <div className="max-w-4xl mx-auto shadow-2xl rounded-sm overflow-hidden border border-brand-light/30">
      {renderHeader()}
      <div className="p-4 bg-white">
        {renderDays()}
        {renderCells()}
        
        <div className="mt-8 flex flex-wrap justify-center gap-6 pb-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-white border border-brand-light/30 rounded-sm"></div>
            <span className="text-[10px] uppercase tracking-widest text-brand-sage">Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-50 border border-red-100 rounded-sm relative overflow-hidden">
              <div className="w-full h-[1px] bg-red-400 rotate-45 absolute top-1/2"></div>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-brand-sage">Fully Booked</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-brand-cream border border-brand-olive rounded-sm flex items-center justify-center">
              <div className="w-1.5 h-1.5 bg-brand-olive rounded-full"></div>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-brand-sage">Today</span>
          </div>
        </div>

        <div className="mt-4 p-4 bg-brand-cream/50 rounded-sm flex items-start gap-3 border-l-4 border-brand-olive">
          <SafeIcon icon={FiInfo} className="text-brand-olive mt-0.5" />
          <p className="text-xs text-brand-sage leading-relaxed italic">
            Calendar is updated in real-time. For festival dates like Pahiyas (May 15), we recommend booking at least 3 months in advance.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;